/**
 * system-arrival-warnings.ts — v4M12.2 defender arrival-angle preview,
 * system-view variant. Sibling to `galaxy-arrival-warnings.ts` (v4M12.1)
 * — same visibility rule (helper in `src/game/jump-prediction.ts`,
 * locked at `docs/TECHTREE.md §9.1`), different render surface.
 *
 * Draws empire-coloured inward chevrons on the focused system's r=0.48
 * Jump Area ring for inbound enemy jumps the viewer can see under the
 * Jump Prediction Sensors tech. Reuses the same `drawArrivalMarker`
 * glyph the galaxy-view pass uses so the visual language stays
 * consistent across both views.
 *
 * Call site is `drawSystemView` in `system-view.ts`, just after the
 * boundary ring is stroked (so the chevrons sit ON the ring) and
 * before orbit rings / planets / ships render (so the gameplay focus
 * stays on units).
 */

import { incomingJumpArrivalsForViewer, JUMP_PREDICTION_TECH } from '../game/jump-prediction';
import { drawArrivalMarker } from './galaxy-jump-target';
import type { GameState, SystemId } from '../state/types/index';
import type { UiState } from '../ui/state';
import { viewerEmpireId } from '../ui/state';

/**
 * Draws inbound-enemy arrival chevrons on the focused system's Jump
 * Area ring. No-op when the viewer hasn't researched the tech (early-
 * out avoids the per-tick scanner walk in pre-tech sessions).
 *
 * Parameters mirror what `drawSystemView` already has in scope: the
 * star centre (cx, cy) and the screen-space radius of the r=0.48
 * boundary ring. Computing screen positions here keeps the math
 * self-contained — no extra Viewport / camera math needed.
 */
export function drawSystemArrivalWarnings(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  focusedSystemId: SystemId,
  cx: number,
  cy: number,
  boundaryRadiusScreen: number,
): void {
  const viewerId = viewerEmpireId(state, ui);
  const viewer = state.empires.get(viewerId);
  if (!viewer) return;
  // Early-out: skip the helper call when the tech isn't researched.
  // The helper enforces this too, but checking once here saves the
  // scanner walk on every frame pre-research.
  if (!viewer.researchedTechs.has(JUMP_PREDICTION_TECH)) return;

  const arrivals = incomingJumpArrivalsForViewer(state, viewerId, focusedSystemId);
  if (arrivals.length === 0) return;

  const starScreen = { x: cx, y: cy };
  for (const arrival of arrivals) {
    const attacker = state.empires.get(arrival.attackerEmpireId);
    // Attacker empire missing is a corrupt-state edge — fall back to
    // a neutral red so the chevron still telegraphs "danger".
    const color = attacker?.color ?? '#ff5555';
    const proj = {
      angle: arrival.arrivalAngle,
      x: cx + Math.cos(arrival.arrivalAngle) * boundaryRadiusScreen,
      y: cy + Math.sin(arrival.arrivalAngle) * boundaryRadiusScreen,
      isCenterDeadzone: false,
    };
    drawArrivalMarker(ctx, starScreen, proj, color);
  }
}
